import { useEffect } from "react";

/**
 * Tiny per-page SEO hook — no react-helmet dependency.
 * Updates <title>, meta description, canonical, and OG tags on route change.
 * Restores previous values on unmount so back-navigation stays clean.
 *
 * Usage inside a page component:
 *   useSEO({
 *     title: "Search 15,000+ India GCC Jobs — KARM",
 *     description: "Live GCC openings across Bengaluru, Hyderabad, Pune…",
 *     path: "/jobs",
 *   });
 */
const CANONICAL_ORIGIN = "https://karm.day";

function upsertMeta(selector, attr, name, content) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

export function useSEO({ title, description, path, noindex } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    if (description) {
      upsertMeta('meta[name="description"]', "name", "description", description);
      upsertMeta('meta[property="og:description"]', "property", "og:description", description);
      upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    }
    if (title) {
      upsertMeta('meta[property="og:title"]', "property", "og:title", title);
      upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    }
    if (path) {
      const abs = `${CANONICAL_ORIGIN}${path.startsWith("/") ? path : "/" + path}`;
      upsertLink("canonical", abs);
      upsertMeta('meta[property="og:url"]', "property", "og:url", abs);
    }
    // Tell Google to drop a page from the index (e.g. a job that's since
    // closed at the source). Reset to "index,follow" on unmount so a
    // noindex flag never leaks onto the next route rendered in its place.
    const robots = upsertMeta('meta[name="robots"]', "name", "robots", noindex ? "noindex,follow" : "index,follow");

    return () => {
      document.title = prevTitle;
      // Not restoring most meta attrs — next route's useSEO overwrites cleanly.
      // robots is the one exception worth resetting defensively (see above).
      robots.setAttribute("content", "index,follow");
    };
  }, [title, description, path, noindex]);
}
